import {
  PropsWithChildren,
  useCallback,
  useMemo,
} from "react";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { Cross1Icon } from "@radix-ui/react-icons";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  FilterMenuOption,
  FilterMenuItemOption,
  isFilterMenuItemOption,
  MultiFilterMenuItemType,
  isFilterMenuItemOptionGroup,
} from "../FilterMenu.model";
import {
  InFilter,
  IsNullFilter,
  isNullFilter,
  NotInFilter,
} from "../FieldFilter.model";
import styles from "../FilterMenu.module.scss";
import { MultiFilterMenuOptions } from "../filter-menu-item/MultiFilterMenuOptions";

function getFlatOptions(options: FilterMenuOption[]): FilterMenuItemOption[] {
  const result: FilterMenuItemOption[] = [];

  function flattenOptions(option: FilterMenuOption) {
    if (isFilterMenuItemOptionGroup(option)) {
      option.options.forEach(flattenOptions);
    } else if (isFilterMenuItemOption(option)) {
      result.push(option);
    }
  }

  options.forEach(flattenOptions);

  return result;
}

interface MultiOptionsProps {
  label: string;
  options: FilterMenuItemOption[];
  selected?: string[];
  searchable?: boolean;
  onClick: (value: string) => void;
}

function MultiOptions({
  children,
  label,
  options,
  selected = [],
  searchable = false,
  onClick,
}: PropsWithChildren<MultiOptionsProps>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>

      <DropdownMenuContent align="start">
        <MultiFilterMenuOptions
          label={label}
          options={options}
          selected={selected}
          searchable={searchable}
          showCheckboxes
          onOptionClick={onClick}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface MultiActiveFilterItemProps<Field extends string>
  extends MultiFilterMenuItemType<Field> {
  value: InFilter | NotInFilter | IsNullFilter;
  size: "1" | "2" | "3";
  viewOnly: boolean;
  onChange: (item: MultiFilterMenuItemType<Field>) => void;
}

export function MultiActiveFilterItem<Field extends string>({
  size,
  viewOnly,
  disableOperator = false,
  onChange,
  ...item
}: MultiActiveFilterItemProps<Field>) {
  const selectedOptions = useMemo<FilterMenuItemOption[]>(() => {
    if (isNullFilter(item.value)) {
      return [];
    }

    return getFlatOptions(item.options).filter((option) =>
      item.value?.value.includes(option.value)
    );
  }, [item]);

  const handleOperatorChange = useCallback(
    (operator: "in" | "nin") => {
      if (isNullFilter(item.value)) {
        return;
      }

      onChange({
        ...item,
        value: {
          operator,
          value: item.value?.value,
        },
      });
    },
    [item, onChange]
  );

  const handleOptionClick = useCallback(
    (option: string) => {
      if (isNullFilter(item.value)) {
        onChange({
          ...item,
          value: {
            operator: "in",
            value: [option],
          },
        });

        return;
      }

      const nextValue = item.value?.value.includes(option)
        ? item.value?.value.filter((value) => value !== option)
        : [...(item.value?.value || []), option];

      if (nextValue.length === 0) {
        onChange({ ...item, value: undefined });
        return;
      }

      onChange({
        ...item,
        value: {
          operator: item.value?.operator ?? "in",
          value: nextValue,
        },
      });
    },
    [item, onChange]
  );

  const handleClear = useCallback(() => {
    onChange({ ...item, value: undefined });
  }, [item, onChange]);

  if (isNullFilter(item.value)) {
    return (
      <Flex gap={"1px"} className={styles.activeFilter} align="center">
        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
        >
          {item.icon}
          {item.label}
        </div>

        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
        >
          is
        </div>

        <MultiOptions
          label={item.label}
          options={getFlatOptions(item.options)}
          searchable={item.searchable}
          onClick={handleOptionClick}
        >
          <div
            data-accent-color="gray"
            className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
              viewOnly ? "pointer-events-none" : ""
            }`}
          >
            Unknown
          </div>
        </MultiOptions>

        {!viewOnly && (
          <IconButton
            variant="soft"
            color="gray"
            size={size}
            onClick={handleClear}
          >
            <Cross1Icon />
          </IconButton>
        )}
      </Flex>
    );
  }

  if (selectedOptions.length === 0) return <></>;

  return (
    <Flex gap={"1px"} className={styles.activeFilter} align="center">
      <div
        data-accent-color="gray"
        className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
      >
        {item.icon}
        {item.label}
      </div>

      {disableOperator ? (
        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button pointer-events-none`}
        >
          is
        </div>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div
              data-accent-color="gray"
              className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
                viewOnly ? "pointer-events-none" : ""
              }`}
            >
              {item.value?.operator === "in" ? "is" : "is not"}
            </div>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="start">
            <DropdownMenuCheckboxItem
              checked={item.value?.operator === "in"}
              onCheckedChange={() => handleOperatorChange("in")}
            >
              is
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={item.value?.operator === "nin"}
              onCheckedChange={() => handleOperatorChange("nin")}
            >
              is not
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <MultiOptions
        label={item.label}
        options={getFlatOptions(item.options)}
        selected={item.value?.value || []}
        searchable={item.searchable}
        onClick={handleOptionClick}
      >
        <div
          data-accent-color="gray"
          className={`rt-reset rt-BaseButton rt-r-size-${size} rt-variant-soft rt-high-contrast rt-Button ${
            viewOnly ? "pointer-events-none" : ""
          } shrink`}
        >
          <Flex align="center" gap="2">
            <Flex align="center">
              {selectedOptions.map((option, index) => (
                <span
                  key={option.value}
                  style={{ marginLeft: index > 0 ? "-10px" : "0" }}
                >
                  {option.icon}
                </span>
              ))}
            </Flex>

            <Text truncate>
              {selectedOptions.length > 2
                ? `${selectedOptions.length} selected`
                : selectedOptions.map((option) => option.label).join(", ")}
            </Text>
          </Flex>
        </div>
      </MultiOptions>

      {!viewOnly && (
        <IconButton
          variant="soft"
          color="gray"
          size={size}
          onClick={handleClear}
        >
          <Cross1Icon />
        </IconButton>
      )}
    </Flex>
  );
}
